import { useDroppable } from '@dnd-kit/core';
import { Home, FolderOpen, ChevronDown, ChevronRight, MoreVertical, FolderPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface DroppableRootFolderProps {
  onClick: () => void;
  workflowCount?: number;
  expanded?: boolean;
  onToggleExpand?: () => void;
  onAddFolder?: () => void;
  hasSubfolders?: boolean;
}

export function DroppableRootFolder({
  onClick,
  workflowCount,
  expanded = true,
  onToggleExpand,
  onAddFolder,
  hasSubfolders = false,
}: DroppableRootFolderProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: 'root-folder',
    data: {
      type: 'folder',
      folderId: null,
    },
  });

  return (
    <div className="relative">
      <div
        ref={setNodeRef}
        className={cn(
          'transition-all duration-200',
          isOver && 'bg-primary/10 ring-2 ring-primary rounded-md'
        )}
      >
        <div className="group relative flex items-center -ml-4">
          {/* Chevron for expand/collapse - outside button */}
          {hasSubfolders && onToggleExpand ? (
            <span
              onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
              className="cursor-pointer p-0.5 hover:bg-muted rounded flex-shrink-0"
            >
              {expanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </span>
          ) : (
            <span className="w-4 flex-shrink-0" />
          )}
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'w-full justify-start h-8 text-xs pr-8',
              isOver && 'bg-primary/5'
            )}
            onClick={onClick}
          >
            {isOver ? (
              <FolderOpen className="h-4 w-4 mr-2 text-primary flex-shrink-0" />
            ) : (
              <Home className="h-4 w-4 mr-2 flex-shrink-0" />
            )}
            <span className="flex-1 text-left truncate max-w-[100px]">My Workflows</span>
          </Button>
          {/* Count and menu icon in same position */}
          {workflowCount !== undefined && workflowCount > 0 && (
            <span className="absolute right-3 text-xs text-muted-foreground group-hover:hidden w-5 text-center">
              {workflowCount}
            </span>
          )}
          {onAddFolder && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-3 h-5 w-5 bg-background opacity-0 group-hover:opacity-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onAddFolder}>
                  <FolderPlus className="mr-2 h-4 w-4" />
                  Add Folder
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {isOver && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 border-2 border-primary border-dashed rounded-md animate-pulse" />
        </div>
      )}
    </div>
  );
}
